import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { 
  Bell, 
  Bot,
  Check, 
  MessageSquare, 
  Play, 
  Send,
  CheckCircle2,
  XCircle,
  Paperclip,
  Clock,
  Volume2,
  VolumeX
} from 'lucide-react';
import { API_URL as API } from '../utils/api';

const TIPO_ICONS = {
  tarefa_iniciada: Play,
  tarefa_finalizada: Send,
  tarefa_aprovada: CheckCircle2,
  tarefa_reprovada: XCircle,
  tarefa_pendente: Clock,
  comentario: MessageSquare,
  anexo: Paperclip,
  mannu_alerta: Bot,
  mannu_correcao: Bot,
  mannu_execucao: Bot,
  mannu_documento: Bot,
};

const TIPO_COLORS = {
  tarefa_iniciada: 'bg-blue-100 text-blue-600',
  tarefa_finalizada: 'bg-purple-100 text-purple-600',
  tarefa_aprovada: 'bg-emerald-100 text-emerald-600',
  tarefa_reprovada: 'bg-red-100 text-red-600',
  tarefa_pendente: 'bg-amber-100 text-amber-600',
  comentario: 'bg-slate-100 text-slate-600',
  anexo: 'bg-cyan-100 text-cyan-600',
  mannu_alerta: 'bg-amber-100 text-amber-700',
  mannu_correcao: 'bg-violet-100 text-violet-700',
  mannu_execucao: 'bg-emerald-100 text-emerald-700',
  mannu_documento: 'bg-sky-100 text-sky-700',
};

// Sons de notificação por tipo (usando Web Audio API)
const SOUND_CONFIGS = {
  tarefa_aprovada:   { freq: [880, 1100], type: 'sine', duration: 0.3, gap: 0.1 },
  tarefa_reprovada:  { freq: [400, 300], type: 'square', duration: 0.25, gap: 0.08 },
  tarefa_finalizada: { freq: [660, 880, 1100], type: 'sine', duration: 0.15, gap: 0.08 },
  tarefa_iniciada:   { freq: [520, 660], type: 'triangle', duration: 0.2, gap: 0.1 },
  tarefa_pendente:   { freq: [600], type: 'sine', duration: 0.4, gap: 0 },
  comentario:        { freq: [700, 800], type: 'sine', duration: 0.1, gap: 0.05 },
  anexo:             { freq: [500, 700], type: 'triangle', duration: 0.15, gap: 0.08 },
  default:           { freq: [800], type: 'sine', duration: 0.3, gap: 0 },
};

const playNotificationSound = (tipo) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const config = SOUND_CONFIGS[tipo] || SOUND_CONFIGS.default;
    let time = audioContext.currentTime;

    config.freq.forEach((freq, i) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.connect(gain);
      gain.connect(audioContext.destination);
      osc.frequency.value = freq;
      osc.type = config.type;
      gain.gain.setValueAtTime(0.25, time);
      gain.gain.exponentialRampToValueAtTime(0.01, time + config.duration);
      osc.start(time);
      osc.stop(time + config.duration);
      time += config.duration + config.gap;
    });
  } catch (error) {
    console.log('Som nao disponivel:', error);
  }
};

export default function NotificacoesDropdown({ token, onNavigate, surface = 'light' }) {
  const [notificacoes, setNotificacoes] = useState([]);
  const [count, setCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const prevCountRef = useRef(0);
  const dropdownRef = useRef(null);

  const getNotificationTarget = (notif) => {
    if (String(notif?.tipo || '').startsWith('mannu_')) {
      return {
        kind: 'mannu',
        action: notif.acao_destino || notif.tipo,
        correctionProposalId: notif.correction_proposal_id,
        clientId: notif.cliente_id,
        clientName: notif.cliente_nome,
        title: notif.titulo,
        message: notif.mensagem,
      };
    }

    if (!notif?.tarefa_id) return null;

    if (notif.tipo === 'comentario') return { tarefaId: notif.tarefa_id, tab: 'comentarios' };
    if (notif.tipo === 'anexo') return { tarefaId: notif.tarefa_id, tab: 'anexos' };

    if (['tarefa_iniciada', 'tarefa_finalizada', 'tarefa_aprovada', 'tarefa_reprovada', 'tarefa_pendente'].includes(notif.tipo)) {
      return { tarefaId: notif.tarefa_id, tab: 'historico' };
    }

    return { tarefaId: notif.tarefa_id, tab: 'detalhes' };
  };

  // Função para navegar à tarefa
  const handleNotificacaoClick = (notif) => {
    // Marcar como lida
    if (!notif.lida) {
      marcarComoLida(notif.id);
    }
    
    // Navegar à tarefa se houver tarefa_id
    const target = getNotificationTarget(notif);
    if (target && onNavigate) {
      setIsOpen(false);
      onNavigate(target);
    }
  };

  const loadCount = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/api/notificacoes/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newCount = response.data.count || 0;
      
      // Tocar som se houver novas notificações
      if (newCount > prevCountRef.current && soundEnabled && prevCountRef.current > 0) {
        playNotificationSound('default');
      }
      
      prevCountRef.current = newCount;
      setCount(newCount);
    } catch (error) {
      console.error('Erro ao carregar contagem:', error);
    }
  }, [token, soundEnabled]);

  const loadNotificacoes = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/notificacoes?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificacoes(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
      setNotificacoes([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Carregar contagem inicial e configurar polling
  useEffect(() => {
    if (!token) return;
    
    loadCount();
    // Atualizar a cada 15 segundos para ser mais responsivo
    const interval = setInterval(loadCount, 15000);
    return () => clearInterval(interval);
  }, [loadCount, token]);

  // Carregar notificações quando abrir o dropdown
  useEffect(() => {
    if (isOpen && token) {
      loadNotificacoes();
    }
  }, [isOpen, loadNotificacoes, token]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isOpen]);

  const marcarComoLida = async (id) => {
    try {
      await axios.post(`${API}/api/notificacoes/marcar-lida`, 
        { notificacao_ids: [id] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotificacoes(prev => prev.map(n => 
        n.id === id ? { ...n, lida: true } : n
      ));
      setCount(prev => Math.max(0, prev - 1));
      prevCountRef.current = Math.max(0, prevCountRef.current - 1);
    } catch (error) {
      console.error('Erro ao marcar como lida:', error);
    }
  };

  const marcarTodasComoLidas = async () => {
    try {
      await axios.post(`${API}/api/notificacoes/marcar-todas-lidas`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificacoes(prev => prev.map(n => ({ ...n, lida: true })));
      setCount(0);
      prevCountRef.current = 0;
    } catch (error) {
      console.error('Erro ao marcar todas como lidas:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Agora';
    if (diffMins < 60) return `${diffMins}m atrás`;
    if (diffHours < 24) return `${diffHours}h atrás`;
    if (diffDays < 7) return `${diffDays}d atrás`;
    return date.toLocaleDateString('pt-BR');
  };

  const toggleSound = () => {
    setSoundEnabled(!soundEnabled);
    if (!soundEnabled) {
      playNotificationSound('tarefa_aprovada');
    }
  };

  const buttonTone = surface === 'dark'
    ? (count > 0 ? 'bg-white/10 text-amber-300 hover:bg-white/15' : 'text-slate-300 hover:bg-white/10 hover:text-white')
    : (count > 0 ? 'bg-amber-50 text-amber-600 hover:bg-amber-100' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800');

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Botão do sininho */}
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen((current) => !current);
        }}
        className={`relative flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${buttonTone}`}
        data-testid="notificacoes-btn"
        aria-label="Abrir notificações"
      >
        <Bell size={21} />
        {count > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold text-white">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          <div
            className="fixed right-3 top-20 z-50 max-h-[min(680px,calc(100vh-6rem))] w-[calc(100vw-1.5rem)] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl sm:absolute sm:right-0 sm:top-full sm:mt-2 sm:w-[420px]"
            onClick={(event) => event.stopPropagation()}
            data-testid="notificacoes-dropdown-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-200 p-4">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-slate-900">Notificações</h3>
                <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">{count}</span>
                <button
                  onClick={toggleSound}
                  className={`rounded-md p-1.5 transition hover:bg-slate-100 ${soundEnabled ? 'text-emerald-600' : 'text-slate-400'}`}
                  title={soundEnabled ? 'Som ativado' : 'Som desativado'}
                  aria-label={soundEnabled ? 'Desativar som' : 'Ativar som'}
                >
                  {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
                </button>
              </div>
              {count > 0 && (
                <button
                  onClick={marcarTodasComoLidas}
                  className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  data-testid="notificacoes-mark-all-read-button"
                >
                  Marcar tudo como lido
                </button>
              )}
            </div>

            {/* Lista de notificações */}
            <div className="max-h-[calc(100vh-12rem)] overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-slate-400">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto mb-2"></div>
                  Carregando...
                </div>
              ) : notificacoes.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <Bell size={32} className="mx-auto mb-2 opacity-50" />
                  <p>Nenhuma notificação</p>
                </div>
              ) : (
                notificacoes.map(notif => {
                  const Icon = TIPO_ICONS[notif.tipo] || Bell;
                  const colorClass = TIPO_COLORS[notif.tipo] || 'bg-slate-100 text-slate-600';
                  
                  return (
                    <div
                      key={notif.id}
                      className={`cursor-pointer border-b border-slate-100 p-4 transition-colors hover:bg-slate-50 ${
                        !notif.lida ? 'bg-slate-50' : 'bg-white'
                      }`}
                      onClick={() => handleNotificacaoClick(notif)}
                    >
                      <div className="flex gap-3">
                        <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${colorClass}`}>
                          <Icon size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium text-sm text-slate-800">
                              {notif.titulo}
                            </p>
                            {!notif.lida && <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-blue-500"></span>}
                          </div>
                          <p className="text-sm text-slate-600 mt-0.5 line-clamp-2">
                            {notif.mensagem}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <p className="text-xs text-slate-400">
                              {formatDate(notif.created_at)}
                            </p>
                            {(notif.tarefa_id || String(notif.tipo || '').startsWith('mannu_')) && (
                              <button 
                                className="text-xs font-medium text-slate-700 hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (soundEnabled) playNotificationSound(notif.tipo);
                                  handleNotificacaoClick(notif);
                                }}
                                data-testid={`notif-action-${notif.id}`}
                              >
                                {String(notif.tipo || '').startsWith('mannu_')
                                  ? 'Abrir resolução →'
                                  : notif.tipo === 'comentario'
                                    ? 'Ver comentário →'
                                    : notif.tipo === 'anexo'
                                      ? 'Ver anexo →'
                                      : 'Ver tarefa →'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer */}
            {notificacoes.length > 0 && (
              <div className="border-t border-slate-200 bg-slate-50 p-3 text-center">
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-sm font-medium text-slate-500 hover:text-slate-700"
                >
                  Fechar
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
